import React, { useCallback, useState } from 'react'
import styled from '@mui/material/styles/styled'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import { Box, BoxProps } from '../Box'
import { FormControl, FormLabel } from '../Form'
import { ModeItem } from './ModeItem'
import { useLoginUrl, useMe } from 'hooks'
import { AppName } from 'hooks/useBaseUrl'
import {
  Market as MarketIcon,
  Campus as CampusIcon,
  Kitchen as KitchenIcon,
  Kiosk as KioskIcon,
  Admin as AdminIcon,
} from 'icons'
import { ExpandMore } from '@mui/icons-material'

export type ModeSelectorProps = BoxProps & {
  appName: AppName
  onSelect: (value: AppName) => void
}

export const StyledSelector = styled(Box, {
  // shouldForwardProp: (prop) => true,
})<ModeSelectorProps>(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  marginBottom: theme.spacing(1),
  borderBottomWidth: '1px',
  borderBottomColor: theme.palette.divider,
  borderBottomStyle: 'solid',
  '& .MuiOutlinedInput-notchedOutline': {
    borderWidth: 0,
    borderColor: theme.palette.divider,
  },
  '& .MuiOutlinedInput-input': {
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingLeft: 0, // '16px',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px',
    borderColor: theme.palette.divider,
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderWidth: 0,
    borderColor: theme.palette.divider,
  },
  '& .MuiOutlinedInput-root.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px',
    borderColor: theme.palette.divider,
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-input': {
    backgroundColor: theme.palette.grey[100],
  },
  '& .MuiOutlinedInput-root.Mui-focused:hover .MuiOutlinedInput-input': {
    backgroundColor: theme.palette.background.default,
  },
  '& .MuiFormLabel-root': {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
  },
}))

export const ModeSelector: React.FC<ModeSelectorProps> = ({ appName, onSelect, ...props }) => {
  const { hasMarketAccess, hasKioskAccess, hasKitchenAccess, hasCampusAccess, hasLuxoAccess, hasOtherAccess } = useMe()
  const marketLoginUrl = useLoginUrl('market')
  const campusLoginUrl = useLoginUrl('campus')
  const kitchenLoginUrl = useLoginUrl('kitchen')
  const kioskLoginUrl = useLoginUrl('kiosk')
  const luxoLoginUrl = useLoginUrl('luxo')
  const [value, setValue] = useState<AppName>(appName)
  const redirectUrl = useCallback((val: AppName) => {
    switch (val) {
      case "market":
        return marketLoginUrl
      case "kiosk":
        return kioskLoginUrl
      case "kitchen":
        return kitchenLoginUrl
      case "campus":
        return campusLoginUrl
      case "luxo":
        return luxoLoginUrl
      default:
        return window.location.href
    }
  }, [marketLoginUrl, kioskLoginUrl, kitchenLoginUrl, campusLoginUrl, luxoLoginUrl])

  if (!hasOtherAccess(appName)) return null

  return <StyledSelector {...props}>
    <FormControl fullWidth variant="outlined">
      <FormLabel id="mode-selector-label">Mode</FormLabel>
      <Select
        labelId="mode-selector-label"
        variant="outlined"
        defaultValue="kiosk"
        value={value}
        onChange={(e) => {
          onSelect(e.target.value as AppName)
          setValue(e.target.value as AppName)
          window.location.href = redirectUrl(e.target.value as AppName)
        }}
        IconComponent={ExpandMore}
        MenuProps={{
          sx: {
            mt: 1,
            '& .MuiPaper-root': {
              border: 'none',
            },
            '& .MuiList-root': {
              py: 0,
            },
            '& .MuiMenuItem-root': {
              px: 0,
            },
            '& .MuiMenuItem-root:first-child': {
              pt: 1.25,
            },
            '& .MuiMenuItem-root:last-child': {
              pb: 1.25,
            },
          },
        }}
      >

        { hasKioskAccess && <MenuItem value="kiosk" sx={{ display: (value === 'kiosk' ? 'none' : 'flex')}}>
          <ModeItem
            icon={<KioskIcon fontSize="small" />}
            color={(theme) => theme.palette.kiosk.dark}
            backgroundColor={(theme) => theme.palette.kiosk.light}
            title="Cafeteria Kiosk"
          />
        </MenuItem> }

        { hasKitchenAccess && <MenuItem value="kitchen" sx={{ display: (value === 'kitchen' ? 'none' : 'flex')}}>
          <ModeItem
            icon={<KitchenIcon fontSize="small" />}
            color={(theme) => theme.palette.kitchen.dark}
            backgroundColor={(theme) => theme.palette.kitchen.light}
            title="Kitchen Manager"
          />
        </MenuItem> }

        { hasCampusAccess && <MenuItem value="campus" sx={{ display: (value === 'campus' ? 'none' : 'flex')}}>
          <ModeItem
            icon={<CampusIcon fontSize="small" />}
            color={(theme) => theme.palette.campus.dark}
            backgroundColor={(theme) => theme.palette.campus.light}
            title="School Admin"
          />
        </MenuItem> }

        { hasMarketAccess && <MenuItem value="market" sx={{ display: (value === 'market' ? 'none' : 'flex')}}>
          <ModeItem
            icon={<MarketIcon fontSize="small" />}
            color={(theme) => theme.palette.market.dark}
            backgroundColor={(theme) => theme.palette.market.light}
            title="Order Meals"
          />
        </MenuItem> }

        { hasLuxoAccess && <MenuItem value="luxo" sx={{ display: (value === 'luxo' ? 'none' : 'flex')}}>
          <ModeItem
            icon={<AdminIcon fontSize="small" />}
            color={(theme) => theme.palette.luxo.main}
            backgroundColor={(theme) => theme.palette.luxo.light}
            title="Ordo Admin"
          />
        </MenuItem> }
      </Select>
    </FormControl>
  </StyledSelector>
}

export default ModeSelector
